import { useEffect, useRef } from 'react';
import { ExchangeOut } from '@/client';
import { useAssistantExchanges } from '@/hooks/assistant/useAssistantExchanges';

/**
 * Deep-link resume: the assistant URL carries ?questionId= to address a past session. When it points
 * at a session that isn't already loaded, fetch that session's exchanges and resume it, so a
 * conversation is reachable directly by URL (a shared/bookmarked link) rather than only via chat
 * history.
 *
 * A deep link may mount with stale cached exchanges while TanStack Query starts a refetch. Keep the
 * restoration open until its first settled, non-streaming result, so an invalidation can reconcile a
 * turn that completes while the restoration is still pending. Applying that result closes the episode.
 */
export const useDeepLinkSessionResume = (
	houseId: number | undefined,
	questionId: string | null | undefined,
	currentSessionId: string | null,
	isStreaming: boolean,
	resumeSession: (sessionId: string, exchanges: ExchangeOut[]) => void,
): void => {
	const restoringSessionIdRef = useRef<string | null>(null);
	const appliedExchangesRef = useRef<ExchangeOut[] | undefined>(undefined);
	const isRestorationPending = questionId != null && questionId !== currentSessionId;
	const shouldObserveSession = isRestorationPending || restoringSessionIdRef.current === questionId;
	const resumeSessionId = shouldObserveSession ? questionId : null;
	const {
		data: exchanges,
		isFetching,
		isSuccess,
	} = useAssistantExchanges(houseId ?? 0, resumeSessionId, { reconcileOnMount: true });

	useEffect(() => {
		restoringSessionIdRef.current = null;
		appliedExchangesRef.current = undefined;
	}, [questionId]);

	useEffect(() => {
		if (isRestorationPending) {
			restoringSessionIdRef.current = questionId;
		}
	}, [isRestorationPending, questionId]);

	useEffect(() => {
		if (
			!resumeSessionId ||
			isStreaming ||
			isFetching ||
			!isSuccess ||
			!exchanges ||
			exchanges.length === 0 ||
			exchanges === appliedExchangesRef.current
		) {
			return;
		}
		restoringSessionIdRef.current = null;
		appliedExchangesRef.current = exchanges;
		// Exchanges arrive newest-first; the chat UI renders chronologically.
		resumeSession(resumeSessionId, [...exchanges].reverse());
	}, [exchanges, isFetching, isStreaming, isSuccess, resumeSession, resumeSessionId]);
};
